import React from "react";
import ReactMarkdown from "react-markdown";
import { safeLinkUrl } from "../../util/landingPageMarkdown";

// Renders landing-page markdown (plan §17.4). The twin of
// app/src/components/LandingPageEditor/LandingPageContent.jsx — the two must stay in
// step, because that one is what an owner previews and this one is what a guest gets.
//
// No `rehype-raw` and no `remark-gfm`. Raw HTML being off by default is the entire
// reason to prefer react-markdown here over rolling a renderer, and the allowlist
// below is a second lock on the same door: the stored source has already had its tags
// removed on write, and a mistake in either control alone is not a vulnerability.

const ALLOWED_ELEMENTS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "p",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "a",
  "br",
  "blockquote",
  "hr",
  "code",
  "pre"
];

// Google Tag Manager (GTM-W7PVB5B, loaded from index.html) runs GA4 G-8P82LBLPQX with
// cross-domain linking configured for waitly.com, app.waitly.com, wl.waitly.com and
// www.waitly.com. Its linker registers bubble-phase `mousedown` and `keyup` listeners
// on `document` and, when the event lands inside an anchor pointing at one of those
// hosts, rewrites that anchor's href to carry a `_gl=1*...` session blob before the
// navigation happens.
//
// That is right for a funnel hop and wrong here: a landing page is text the owner
// wrote for a guest, and the guest should get the URL that was typed. React dispatches
// this handler at the root container, which is a descendant of `document`, so stopping
// the event here means the linker never sees it — the href stays clean for a plain
// click, a cmd-click and "copy link address" alike, rather than only for the one path
// an onClick override could cover.
const stopLinkDecoration = (event: React.SyntheticEvent) => event.stopPropagation();

// `rel="nofollow"` alongside noopener/noreferrer: this is owner-authored text on our
// domain, and we are not vouching for wherever it points.
const components = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      onMouseDown={stopLinkDecoration}
      onKeyUp={stopLinkDecoration}
    >
      {children}
    </a>
  )
};

const LandingPageContent = ({ content, className }: { content?: string; className?: string }) => (
  <div className={className}>
    <ReactMarkdown
      allowedElements={ALLOWED_ELEMENTS}
      unwrapDisallowed
      urlTransform={safeLinkUrl}
      components={components}
    >
      {content || ""}
    </ReactMarkdown>
  </div>
);

export default LandingPageContent;
